// React
import { useState } from 'react';
import { Link } from '@tanstack/react-router';

// Components
import { CTAButton } from '../../components/ui/CTAButton/CTAButton';
import { SEO } from '../../components/SEO/SEO';

// Types
type AnimatedCharacterProps = {
  char: string;
  index: number;
};

const AnimatedCharacter = ({ char, index }: AnimatedCharacterProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <span
      className="inline-block cursor-default transition-all duration-300 ease-out"
      style={{
        transform: isHovered ? 'translateY(-12px) rotate(-5deg) scale(1.1)' : 'translateY(0) rotate(0) scale(1)',
        color: isHovered ? 'var(--color-orange-800)' : 'var(--color-blue-1000)',
        transitionDelay: `${index * 30}ms`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="presentation"
    >
      {char}
    </span>
  );
};

export const NotFoundPage = () => {
  const characters = ['4', '0', '4'];

  return (
    <div className="flex min-h-[calc(100vh-80px)] flex-col items-center justify-center px-ds-24 py-ds-96">
      <SEO
        title="Page Not Found | Archie"
        description="The page you're looking for doesn't exist or has been moved."
        canonicalUrl="/404"
      />

      <div className="flex flex-col items-center gap-ds-32 text-center">
        {/* Animated 404 */}
        <h1 className="font-heading text-[120px] font-light leading-none tracking-tight text-blue-1000 md:text-[180px] lg:text-[220px]">
          {characters.map((char, index) => (
            <AnimatedCharacter key={index} char={char} index={index} />
          ))}
        </h1>

        {/* Message */}
        <div className="flex max-w-md flex-col gap-ds-16">
          <h2 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light text-blue-1000 pt-4 pb-6">
            Page not found
          </h2>
          <p className="font-body text-base text-gray-800">
            The page you're looking for doesn't exist or has been moved. Let's get you back on track.
          </p>
        </div>

        {/* Back to Home Button */}
        <Link to="/" className="pt-10">
          <CTAButton variant="primary">
            Back to Home
          </CTAButton>
        </Link>
      </div>
    </div>
  );
};
